import * as React from "react";
import { Patient } from "../components/order-components/Patient";
import { PartialDentureInfo } from "../components/order-components/pap-workflow-components/PartialDentureInfo";
import { HooksAndOptions } from "../components/order-components/pap-workflow-components/HooksAndOptions";
import { MaterialAndShadeSelection } from "../components/order-components/pap-workflow-components/MaterialAndShadeSelection";
import { PartialDentureWorkflow } from "../components/order-components/pap-workflow-components/PartialDentureWorkflow";
import { getAllTeeths, type Rdv2PreviousProducts } from "./product.utils";
import { Order, Product, RemovableItem, User } from "../types/interfaces";
import {
  DeliveryDateType,
  ExtendedOrder,
  GetDeliveryDateType,
  OrderRecap,
} from "../components/order-components/newOrderRecap";
import { RemovableItemOrderType, ItemType } from "../types/enums";
import { Typology } from "../components/order-components/pac-workflow-components/Typology";
import { MaterialAndShade } from "../components/order-components/pac-workflow-components/MaterialAndShade";
import { TOGGLE_FEATURE } from "../types/constants";
import { CreateRemovableItemFullDentureImprintChoice } from "../components/order-components/pac-workflow-components/ImprintChoice";
import { CreateRemovableItemFullDentureOrderTypeChoice } from "../components/order-components/pac-workflow-components/OrderTypeChoice";
import { CreateRemovableItemFullDentureMaterial } from "../components/order-components/pac-workflow-components/Material";
import { CreateRemovableItemFullDentureAesthetics } from "../components/order-components/pac-workflow-components/Aesthetics";
import { CreateRemovableItemPartialDentureOrderTypeChoice } from "../components/order-components/pap-workflow-components/OrderTypeChoice";
import { CreateRemovableItemPartialDentureMaterial } from "../components/order-components/pap-workflow-components/Material";
import { CreateRemovableItemPartialDentureAesthetics } from "../components/order-components/pap-workflow-components/Aesthetics";

import { ImprintChoiceRef } from "../components/order-components/pac-workflow-components/ImprintChoice";
import { OrderTypeChoiceRef } from "../components/order-components/pac-workflow-components/OrderTypeChoice";
import { MaterialRef } from "../components/order-components/pac-workflow-components/Material";
import { AestheticsRef } from "../components/order-components/pac-workflow-components/Aesthetics";
import { PartialDentureOrderTypeChoiceRef } from "../components/order-components/pap-workflow-components/OrderTypeChoice";
import { PartialDentureMaterialRef } from "../components/order-components/pap-workflow-components/Material";
import { PartialDentureAestheticsRef } from "../components/order-components/pap-workflow-components/Aesthetics";
import { shouldShowAestheticsStep } from "../components/order-components/pac-workflow-components/fullDenture.constants";
import { shouldShowPapAestheticsStep } from "../components/order-components/pap-workflow-components/partialDenture.constants";
import {
  Rdv2Choice,
  Rdv2ChoiceRef,
} from "../components/order-components/pac-workflow-components/Rdv2Choice";
import {
  TryInFeedback,
  TryInFeedbackRef,
} from "../components/order-components/pac-workflow-components/TryInFeedback";

import {
  PartialDentureRdv2Choice,
  PartialDentureRdv2ChoiceRef,
} from "../components/order-components/pap-workflow-components/Rdv2Choice";

import {
  PartialDentureTryInFeedback,
  PartialDentureTryInFeedbackRef,
} from "../components/order-components/pap-workflow-components/TryInFeedback";

export type FullDentureRefs = {
  imprintChoiceRef: React.RefObject<ImprintChoiceRef>;
  orderTypeChoiceRef: React.RefObject<OrderTypeChoiceRef>;
  materialRef: React.RefObject<MaterialRef>;
  aestheticsRef: React.RefObject<AestheticsRef>;
  rdv2ChoiceRef: React.RefObject<Rdv2ChoiceRef>;
  tryInFeedbackRef: React.RefObject<TryInFeedbackRef>;
};

export type PartialDentureRefs = {
  orderTypeChoiceRef: React.RefObject<PartialDentureOrderTypeChoiceRef>;
  materialRef: React.RefObject<PartialDentureMaterialRef>;
  aestheticsRef: React.RefObject<PartialDentureAestheticsRef>;
  rdv2ChoiceRef?: React.RefObject<PartialDentureRdv2ChoiceRef>;
  tryInFeedbackRef?: React.RefObject<PartialDentureTryInFeedbackRef>;
};

export type WorkflowStep = {
  label: string;
  description?: string;
  component: React.ReactNode;
  onNext?: () => Promise<void>;
  onPrev?: () => void;
};

export enum PartialDentureOptions {
  METAL_MESH = "Grille de renfort métallique",
  LINGUAL_METAL_BAND = "Bandeau métallique lingual",
  PALATAL_METAL_BAND = "Bandeau métallique palatal",
  CUSTOM_TRAY = "Porte-empreinte individuel",
}

export enum HookPlacementBy {
  JASPER = "JASPER",
  DOCTOR = "DOCTOR",
}

export const getDisabledTeeth = (notDisabledTeeth: number[]) => {
  return getAllTeeths().filter(tooth => !notDisabledTeeth.includes(tooth));
};

export const workflowDefaultValues = {
  FULL_DENTURE: {
    defaultValues: (order?: Order, product?: Product) => {
      const newRemovableItem = product?.removableItem?.[0];
      return {
        firstName: order?.patient?.firstName,
        lastName: order?.patient?.lastName,
        birthDate: order?.patient?.birthdate,
        doctorId: order?.user?.id,
        sex: order?.patient?.sex,
        teeth: product?.teeth ?? [],
        imprintType: newRemovableItem?.imprintType ?? "",
        linkToExistingOrder: false,
        linkedOrderId: newRemovableItem?.linkedOrderId ?? "",
        fullDentureOrderType: newRemovableItem?.fullDentureOrderType ?? "",
        itemMaterial: newRemovableItem?.itemMaterial?.id ?? "",
        individualImpressionTray:
          newRemovableItem?.individualImpressionTray ?? false,
        isBimaxillarySeparated:
          newRemovableItem?.isBimaxillarySeparated ?? false,
        teethShadeId: newRemovableItem?.teethShade?.id ?? "",
        gingivaShadeId: newRemovableItem?.gingivaShade?.id ?? "",
        teethShape: product?.itemPreferences?.teethShape ?? "",
        shadeType: product?.itemPreferences?.shadeType ?? "",
        comment: "",
        deliveryDate: null,
      };
    },
  },
  PARTIAL_DENTURE: {
    defaultValues: (order?: Order, product?: Product) => {
      const newRemovableItem = product?.removableItem?.[0];
      return {
        firstName: order?.patient?.firstName,
        lastName: order?.patient?.lastName,
        birthDate: order?.patient?.birthdate,
        doctorId: order?.user?.id,
        sex: order?.patient?.sex,
        partialDentureOrderType:
          newRemovableItem?.partialDentureOrderType ?? "",
        isReplacement: false,
        teeths: {
          missingTeeth: newRemovableItem?.partialDenture?.missingTeeth ?? [],
          toExtract: newRemovableItem?.teethToManufacture ?? [],
        },
        material0: "",
        brand0: "",
        shade0: "",
        gingivaShade0: "",
        material1: "",
        brand1: "",
        shade1: "",
        gingivaShade1: "",
        toggleMultiItem: false,
        hookChoice: "",
        hooksPosition: [],
        hooksMaterial: "",
        workflow: "",
        comment: "",
        options: [],
        deliveryDate: null,
        itemMaterialId: newRemovableItem?.itemMaterial?.id ?? "",
        customTray: false,
        customTrayArch: null,
        shadeType: product?.itemPreferences?.shadeType ?? "",
        teethShadeId: newRemovableItem?.teethShade?.id ?? "",
        gingivaShadeId: newRemovableItem?.gingivaShade?.id ?? "",
        teethShape: product?.itemPreferences?.teethShape ?? "",
      };
    },
  },
};

const materialAndShadeFields = (removableItems: RemovableItem[]) => {
  return removableItems.reduce(
    (acc, item, index) => ({
      ...acc,
      [`material${index}`]: item.itemMaterial?.id ?? "",
      [`brand${index}`]: item.teethShade?.id ?? "",
      [`shade${index}`]: item.teethShade?.id ?? "",
      [`gingivaShade${index}`]: item.gingivaShade?.id ?? "",
    }),
    {}
  );
};

export const workflowFields = (removableItems: RemovableItem[]) => {
  return {
    FULL_DENTURE: [
      // Step 0: Patient
      {
        firstName: "",
        lastName: "",
        birthDate: "",
        doctorId: "",
        sex: "",
      },
      // Step 1: ArchSelection
      {
        teeth: [],
      },
      // Step 2: ImprintChoice
      {
        imprintType: "",
        linkToExistingOrder: false,
        linkedOrderId: "",
      },
      // Step 3: OrderType
      {
        fullDentureOrderType: "",
      },
      // Step 4: Material
      {
        itemMaterial: "",
        individualImpressionTray: false,
        isBimaxillarySeparated: false,
      },
      // Step 5: Aesthetics
      {
        teethShadeId: "",
        gingivaShadeId: "",
        teethShape: "",
        shadeType: "",
      },
      // Step 6: Recap
      {
        comment: "",
        deliveryDate: null,
      },
    ],
    PARTIAL_DENTURE: [
      {
        firstName: "",
        lastName: "",
        birthDate: "",
        doctorId: "",
        sex: "",
      },
      {
        isReplacement: false,
        teeths: {
          missingTeeth: [],
          toExtract: [],
        },
      },
      {
        workflow: "",
      },
      {
        toggleMultiItem: false,
        ...materialAndShadeFields(removableItems),
      },
      {
        hookChoice: "",
        hooksPosition: [],
        hooksMaterial: "",
        options: [],
      },
      {
        comment: "",
        deliveryDate: null,
      },
    ],
  };
};

export const stepLabels = {
  PATIENT: "Patient",
  RECAP: "Récap",
  PARTIAL_DENTURE: {
    ORDER_TYPE: "Type de commande",
    INFORMATION: "Informations",
    MATERIAL: "Matériau",
    AESTHETICS: "Esthétique",
    HOOKS_AND_OPTIONS: "Crochets et options",
    CHOICE: "Choix",
    TRY_IN_FEEDBACK: "Remarques",
    // old labels kept for retro compatibility
    // to be removed with toggle flag useNewPapWorkflow
    PROTHESIS: "Prothèse",
    FLOW: "Flux de travail",
  },
  FULL_DENTURE: {
    IMPRINT_CHOICE: "Type d'empreinte",
    ORDER_TYPE: "Type de commande",
    MATERIAL: "Détails",
    AESTHETICS: "Esthétique",
    REVIEW: "Récapitulatif",
    TYPOLOGY: "Typologie",
    MATERIAL_AND_SHADE: "Matériau et teinte",
    CHOICE: "Choix",
    TRY_IN_FEEDBACK: "Remarques",
  },
};

export const WORKFLOW_MAP = {
  FULL_DENTURE: {
    steps: (
      removableItemChoicesData?: any,
      nextStep?: () => Promise<void>,
      prevStep?: () => void,
      product?: Product,
      refs?: FullDentureRefs,
      fullDentureOrderType?: RemovableItemOrderType | null,
      isRdv2Continuation?: boolean,
      previousProduct?: Product,
      isLabWorkflow?: boolean,
      order?: Order
    ) => {
      if (isRdv2Continuation) {
        const previousOrderType =
          previousProduct?.removableItem?.[0]?.fullDentureOrderType;
        const isAfterTryIn =
          previousOrderType === RemovableItemOrderType.TRY_IN;
        if (isAfterTryIn) {
          const rdv2TryInSteps = [
            {
              label: stepLabels.FULL_DENTURE.CHOICE,
              component: (
                <Rdv2Choice
                  ref={refs?.rdv2ChoiceRef}
                  product={product}
                  previousProduct={previousProduct}
                  isEditMode={isLabWorkflow}
                  order={order}
                />
              ),
              onNext: () => nextStep(),
              onPrev: () => prevStep(),
              saveData: () => refs?.rdv2ChoiceRef?.current?.saveData(),
            },
            {
              label: stepLabels.FULL_DENTURE.TRY_IN_FEEDBACK,
              component: (
                <TryInFeedback
                  ref={refs?.tryInFeedbackRef}
                  product={product}
                  previousProduct={previousProduct}
                  order={order}
                />
              ),
              onNext: () => nextStep(),
              onPrev: () => prevStep(),
              saveData: () => refs?.tryInFeedbackRef?.current?.saveData(),
            },
          ];

          return rdv2TryInSteps;
        } else {
          const rdv2Steps = [
            {
              label: stepLabels.FULL_DENTURE.CHOICE,
              component: (
                <Rdv2Choice
                  ref={refs?.rdv2ChoiceRef}
                  product={product}
                  isEditMode={isLabWorkflow}
                  order={order}
                />
              ),
              onNext: () => nextStep(),
              onPrev: () => prevStep(),
              saveData: () => refs?.rdv2ChoiceRef?.current?.saveData(),
            },
            {
              label: stepLabels.FULL_DENTURE.MATERIAL,
              component: (
                <CreateRemovableItemFullDentureMaterial
                  ref={refs?.materialRef}
                  product={product}
                  removableItemChoicesData={removableItemChoicesData}
                  isConfirmationMode={true}
                  previousProduct={previousProduct}
                />
              ),
              onNext: () => nextStep(),
              onPrev: () => prevStep(),
              saveData: () => refs?.materialRef?.current?.saveData(),
            },
            {
              label: stepLabels.FULL_DENTURE.AESTHETICS,
              component: (
                <CreateRemovableItemFullDentureAesthetics
                  ref={refs?.aestheticsRef}
                  product={product}
                  removableItemChoicesData={removableItemChoicesData}
                  isConfirmationMode={true}
                  previousProduct={previousProduct}
                />
              ),
              onNext: () => nextStep(),
              onPrev: () => prevStep(),
              saveData: () => refs?.aestheticsRef?.current?.saveData(),
            },
          ];

          if (
            fullDentureOrderType &&
            !shouldShowAestheticsStep(fullDentureOrderType)
          ) {
            return rdv2Steps.filter(
              step => step?.label !== stepLabels.FULL_DENTURE.AESTHETICS
            );
          }

          return rdv2Steps;
        }
      }

      // RDV 1 flow
      const allSteps = [
        {
          label: stepLabels.FULL_DENTURE.IMPRINT_CHOICE,
          component: (
            <CreateRemovableItemFullDentureImprintChoice
              ref={refs?.imprintChoiceRef}
              product={product}
            />
          ),
          onNext: () => nextStep(),
          onPrev: () => prevStep(),
          saveData: () => refs?.imprintChoiceRef?.current?.saveData(),
        },
        {
          label: stepLabels.FULL_DENTURE.ORDER_TYPE,
          component: (
            <CreateRemovableItemFullDentureOrderTypeChoice
              ref={refs?.orderTypeChoiceRef}
              product={product}
            />
          ),
          onNext: () => nextStep(),
          onPrev: () => prevStep(),
          saveData: () => refs?.orderTypeChoiceRef?.current?.saveData(),
        },
        {
          label: stepLabels.FULL_DENTURE.MATERIAL,
          component: (
            <CreateRemovableItemFullDentureMaterial
              ref={refs?.materialRef}
              product={product}
              removableItemChoicesData={removableItemChoicesData}
            />
          ),
          onNext: () => nextStep(),
          onPrev: () => prevStep(),
          saveData: () => refs?.materialRef?.current?.saveData(),
        },
        {
          label: stepLabels.FULL_DENTURE.AESTHETICS,
          component: (
            <CreateRemovableItemFullDentureAesthetics
              ref={refs?.aestheticsRef}
              product={product}
              removableItemChoicesData={removableItemChoicesData}
            />
          ),
          onNext: () => nextStep(),
          onPrev: () => prevStep(),
          saveData: () => refs?.aestheticsRef?.current?.saveData(),
        },
      ];

      if (
        fullDentureOrderType &&
        !shouldShowAestheticsStep(fullDentureOrderType)
      ) {
        return allSteps.filter(
          step => step?.label !== stepLabels.FULL_DENTURE.AESTHETICS
        );
      }

      return allSteps;
    },
  },
  PARTIAL_DENTURE: {
    steps: (
      removableItemChoicesData?: any,
      nextStep?: () => Promise<void>,
      prevStep?: () => void,
      product?: Product,
      refs?: any,
      orderType?: any,
      isRdv2Continuation?: boolean,
      previousProduct?: Product,
      isLabWorkflow?: boolean,
      order?: Order,
      useNewPapWorkflow?: boolean,
      partialDentureRefs?: PartialDentureRefs,
      partialDentureOrderType?: RemovableItemOrderType | null
    ) => {
      if (isRdv2Continuation && useNewPapWorkflow) {
        const previousOrderType =
          previousProduct?.removableItem?.[0]?.partialDentureOrderType;
        const isAfterTryIn =
          previousOrderType === RemovableItemOrderType.TRY_IN;

        if (isAfterTryIn) {
          return [
            {
              label: stepLabels.PARTIAL_DENTURE.CHOICE,
              component: (
                <PartialDentureRdv2Choice
                  ref={partialDentureRefs?.rdv2ChoiceRef}
                  product={product}
                  previousProduct={previousProduct}
                  isEditMode={isLabWorkflow}
                  order={order}
                />
              ),
              onNext: () => nextStep(),
              onPrev: () => prevStep(),
              saveData: () =>
                partialDentureRefs?.rdv2ChoiceRef?.current?.saveData(),
            },
            {
              label: stepLabels.PARTIAL_DENTURE.TRY_IN_FEEDBACK,
              component: (
                <PartialDentureTryInFeedback
                  ref={partialDentureRefs?.tryInFeedbackRef}
                  product={product}
                  previousProduct={previousProduct}
                  order={order}
                />
              ),
              onNext: () => nextStep(),
              onPrev: () => prevStep(),
              saveData: () =>
                partialDentureRefs?.tryInFeedbackRef?.current?.saveData(),
            },
          ];
        } else {
          // After WAX_BITE
          const rdv2Steps = [
            {
              label: stepLabels.PARTIAL_DENTURE.CHOICE,
              component: (
                <PartialDentureRdv2Choice
                  ref={partialDentureRefs?.rdv2ChoiceRef}
                  product={product}
                  previousProduct={previousProduct}
                  isEditMode={isLabWorkflow}
                  order={order}
                />
              ),
              onNext: () => nextStep(),
              onPrev: () => prevStep(),
              saveData: () =>
                partialDentureRefs?.rdv2ChoiceRef?.current?.saveData(),
            },
            {
              label: stepLabels.PARTIAL_DENTURE.MATERIAL,
              component: (
                <CreateRemovableItemPartialDentureMaterial
                  ref={partialDentureRefs?.materialRef}
                  product={product}
                  removableItemChoicesData={removableItemChoicesData}
                  isConfirmationMode={true}
                  previousProduct={previousProduct}
                />
              ),
              onNext: () => nextStep(),
              onPrev: () => prevStep(),
              saveData: () =>
                partialDentureRefs?.materialRef?.current?.saveData(),
            },
            {
              label: stepLabels.PARTIAL_DENTURE.AESTHETICS,
              component: (
                <CreateRemovableItemPartialDentureAesthetics
                  ref={partialDentureRefs?.aestheticsRef}
                  product={product}
                  removableItemChoicesData={removableItemChoicesData}
                  isConfirmationMode={true}
                  previousProduct={previousProduct}
                />
              ),
              onNext: () => nextStep(),
              onPrev: () => prevStep(),
              saveData: () =>
                partialDentureRefs?.aestheticsRef?.current?.saveData(),
            },
          ];

          if (
            partialDentureOrderType &&
            !shouldShowPapAestheticsStep(partialDentureOrderType)
          ) {
            return rdv2Steps.filter(
              step => step.label !== stepLabels.PARTIAL_DENTURE.AESTHETICS
            );
          }

          return rdv2Steps;
        }
      }
      // old workflow
      if (!useNewPapWorkflow) {
        return [
          {
            label: stepLabels.PARTIAL_DENTURE.INFORMATION,
            component: <PartialDentureInfo product={product} />,
            onNext: () => nextStep(),
            onPrev: () => prevStep(),
          },
          {
            label: stepLabels.PARTIAL_DENTURE.FLOW,
            component: <PartialDentureWorkflow product={product} />,
            onNext: () => nextStep(),
          },
          {
            label: stepLabels.PARTIAL_DENTURE.PROTHESIS,
            component: (
              <MaterialAndShadeSelection
                removableItemChoicesData={removableItemChoicesData}
                product={product}
              />
            ),
            onNext: () => nextStep(),
          },
          {
            label: stepLabels.PARTIAL_DENTURE.HOOKS_AND_OPTIONS,
            component: <HooksAndOptions product={product} />,
            onNext: () => nextStep(),
          },
        ];
      }

      // new pap workflow
      const allSteps = [
        {
          label: stepLabels.PARTIAL_DENTURE.ORDER_TYPE,
          component: (
            <CreateRemovableItemPartialDentureOrderTypeChoice
              ref={partialDentureRefs?.orderTypeChoiceRef}
              product={product}
            />
          ),
          onNext: () => nextStep(),
          onPrev: () => prevStep(),
          saveData: () =>
            partialDentureRefs?.orderTypeChoiceRef?.current?.saveData(),
        },
        {
          label: stepLabels.PARTIAL_DENTURE.INFORMATION,
          component: <PartialDentureInfo product={product} />,
          onNext: () => nextStep(),
          onPrev: () => prevStep(),
        },
        {
          label: stepLabels.PARTIAL_DENTURE.MATERIAL,
          component: (
            <CreateRemovableItemPartialDentureMaterial
              ref={partialDentureRefs?.materialRef}
              product={product}
              removableItemChoicesData={removableItemChoicesData}
            />
          ),
          onNext: () => nextStep(),
          onPrev: () => prevStep(),
          saveData: () => partialDentureRefs?.materialRef?.current?.saveData(),
        },
        {
          label: stepLabels.PARTIAL_DENTURE.AESTHETICS,
          component: (
            <CreateRemovableItemPartialDentureAesthetics
              ref={partialDentureRefs?.aestheticsRef}
              product={product}
              removableItemChoicesData={removableItemChoicesData}
            />
          ),
          onNext: () => nextStep(),
          onPrev: () => prevStep(),
          saveData: () =>
            partialDentureRefs?.aestheticsRef?.current?.saveData(),
        },
        {
          label: stepLabels.PARTIAL_DENTURE.HOOKS_AND_OPTIONS,
          component: <HooksAndOptions product={product} hideCustomTray />,
          onNext: () => nextStep(),
          onPrev: () => prevStep(),
        },
      ];

      if (
        partialDentureOrderType &&
        !shouldShowPapAestheticsStep(partialDentureOrderType)
      ) {
        return allSteps.filter(
          step => step.label !== stepLabels.PARTIAL_DENTURE.AESTHETICS
        );
      }

      return allSteps;
    },
  },
};

export const getFullDentureWorkflowFields = (
  removableItems: RemovableItem[],
  fullDentureOrderType?: RemovableItemOrderType | null
) => {
  const baseFields = [
    // Step 0: Patient
    {
      firstName: "",
      lastName: "",
      birthDate: "",
      doctorId: "",
      sex: "",
    },
    // Step 1: ArchSelection (skipped)
    {
      teeth: [],
    },
    // Step 2: ImprintChoice
    {
      imprintType: "",
      linkToExistingOrder: false,
      linkedOrderId: "",
    },
    // Step 3: OrderType
    {
      fullDentureOrderType: "",
    },
    // Step 4: Material
    {
      itemMaterial: "",
      individualImpressionTray: false,
      isBimaxillarySeparated: false,
    },
    // Step 5: Aesthetics
    {
      teethShadeId: "",
      gingivaShadeId: "",
      teethShape: "",
      shadeType: "",
    },
    // Step 6: Recap
    {
      comment: "",
      deliveryDate: null,
    },
  ];

  // Filter Aesthetics step (index 5) if WAX_BITE
  if (fullDentureOrderType && !shouldShowAestheticsStep(fullDentureOrderType)) {
    return baseFields.filter((_, index) => index !== 5);
  }

  return baseFields;
};

export const getPartialDentureWorkflowFields = (
  removableItems: RemovableItem[],
  partialDentureOrderType?: RemovableItemOrderType | null
) => {
  const baseFields = [
    // Step 0 : Patient
    {
      firstName: "",
      lastName: "",
      birthDate: "",
      doctorId: "",
      sex: "",
    },
    // Step 1: OrderType
    {
      partialDentureOrderType: "",
    },
    // Step 2: Information (teeth selection)
    {
      teeths: {
        missingTeeth: [],
        toExtract: [],
      },
    },
    // Step 3: Material
    {
      itemMaterialId: "",
      customTray: false,
      customTrayArch: null,
    },
    // Step 4: Aesthetics
    {
      shadeType: "",
      teethShadeId: "",
      gingivaShadeId: "",
      teethShape: "",
    },
    // Step 5: HooksAndOptions
    {
      hookChoice: "",
      hooksPosition: [],
      hooksMaterial: "",
      options: [],
    },
    // Step 6: Recap
    {
      comment: "",
      deliveryDate: null,
    },
  ];

  // Filter Aesthetics step (index 4) if WAX_BITE
  if (
    partialDentureOrderType &&
    !shouldShowPapAestheticsStep(partialDentureOrderType)
  ) {
    return baseFields.filter((_, index) => index !== 4);
  }

  return baseFields;
};

export const useWorkflow = (
  cameFromOtherProduct: boolean,
  workflow: string[],
  isLabWorkflow: boolean = false,
  order?: ExtendedOrder,
  user?: User,
  removableItemChoicesData?: any,
  nextStep?: () => Promise<void>,
  prevStep?: () => void,
  product?: Product,
  getDeliveryDate?: GetDeliveryDateType,
  refetchOrder?: () => void,
  setIsShowAddProductModal?: (value: boolean) => void,
  saveOrderRecap?: (
    orderComment: string,
    deliveryDate?: Date,
    updateStatus?: boolean,
    setIsShowAddProductModal?: boolean
  ) => Promise<void>,
  updateDeliveryDate?: DeliveryDateType,
  workflowVersion?: number,
  fullDentureRefs?: FullDentureRefs,
  fullDentureOrderType?: RemovableItemOrderType | null,
  isRdv2Continuation?: boolean,
  previousProducts?: Rdv2PreviousProducts,
  useNewPapWorkflow?: boolean,
  partialDentureRefs?: PartialDentureRefs,
  partialDentureOrderType?: RemovableItemOrderType | null
): WorkflowStep[][] | null => {
  const steps = (workflow ?? []).map((step, index) => {
    const normalizedStep = step.split(" ")[0];

    // For PARTIAL_DENTURE only: resolve the corresponding product by index
    // to handle pap mandibular then adding of pap maxillary
    let resolvedProduct = product;
    if (
      !isRdv2Continuation &&
      normalizedStep === ItemType.PartialDenture &&
      order?.products &&
      order.products.length > 1
    ) {
      const productsOfType = order.products.filter(
        (p: Product) => p.productType === normalizedStep
      );

      let typeIndex = 0;
      for (let i = 0; i < index; i++) {
        if (workflow[i]?.split(" ")[0] === normalizedStep) {
          typeIndex++;
        }
      }

      if (productsOfType[typeIndex]) {
        resolvedProduct = productsOfType[typeIndex] as unknown as Product;
      }
    }
    const values = WORKFLOW_MAP[normalizedStep]?.steps(
      removableItemChoicesData,
      nextStep,
      prevStep,
      resolvedProduct,
      fullDentureRefs,
      fullDentureOrderType,
      isRdv2Continuation,
      previousProducts?.[normalizedStep],
      isLabWorkflow,
      order as Order,
      useNewPapWorkflow,
      partialDentureRefs,
      partialDentureOrderType
    );
    if (!cameFromOtherProduct && index === 0 && !isLabWorkflow) {
      values?.unshift({
        label: stepLabels.PATIENT,
        component: <Patient order={order} user={user} />,
        onNext: () => nextStep(),
      });
    }
    if (index === workflow.length - 1 && !isLabWorkflow) {
      values?.push({
        label: stepLabels.RECAP,
        onPrev: () => prevStep(),
        component: (
          <OrderRecap
            key={`order-recap-${order?.id}-${workflowVersion || 0}`}
            data-testid={`order-recap-${order?.id}-${workflowVersion || 0}`}
            order={order as ExtendedOrder}
            refetchOrder={refetchOrder}
            getDeliveryDate={getDeliveryDate}
            user={
              user as User & {
                accessToken: string;
                customDeliveryDateEnabled: boolean;
              }
            }
            setIsShowAddProductModal={setIsShowAddProductModal}
            saveOrderRecap={saveOrderRecap}
            updateDeliveryDate={updateDeliveryDate}
            isRdv2Continuation={isRdv2Continuation}
          />
        ),
      });
    }
    return values;
  });
  return steps;
};

export const useWorkflowFieldsAndDefaultValues = (params: {
  workflow: string[];
  order?: ExtendedOrder;
  product?: Product;
  cameFromOtherProduct?: boolean;
  isLabWorkflow?: boolean;
  fullDentureOrderType?: RemovableItemOrderType | null;
  isRdv2Continuation?: boolean;
  previousProducts?: Rdv2PreviousProducts;
  useNewPapWorkflow?: boolean;
  partialDentureRefs?: PartialDentureRefs;
  partialDentureOrderType?: RemovableItemOrderType;
}) => {
  const {
    workflow,
    order,
    product,
    cameFromOtherProduct,
    isLabWorkflow = false,
    fullDentureOrderType,
    isRdv2Continuation,
    previousProducts,
    useNewPapWorkflow,
    partialDentureRefs,
    partialDentureOrderType,
  } = params;
  const currentWorkflowFields = workflow.map((step, index) => {
    const normalizedStep = step.split(" ")[0];

    if (normalizedStep === ItemType.FULL_DENTURE) {
      if (isRdv2Continuation && isLabWorkflow) {
        const previousOrderType =
          previousProducts?.[normalizedStep]?.removableItem?.[0]
            ?.fullDentureOrderType;
        const isAfterTryIn =
          previousOrderType === RemovableItemOrderType.TRY_IN;

        if (isAfterTryIn) {
          return [{ rdv2Choice: "" }, { TryInFeedback: "" }];
        } else {
          const rdv2Fields = [
            { rdv2Choice: "" },
            {
              itemMaterial: "",
              individualImpressionTray: false,
              isBimaxillarySeparated: false,
            },
            {
              teethShadeId: "",
              gingivaShadeId: "",
              teethShape: "",
              shadeType: "",
            },
          ];
          if (
            fullDentureOrderType &&
            !shouldShowAestheticsStep(fullDentureOrderType)
          ) {
            return rdv2Fields.filter((_, index) => index !== 2);
          }

          return rdv2Fields;
        }
      }
      const fields = getFullDentureWorkflowFields(
        product?.removableItem ?? [],
        fullDentureOrderType
      );
      if (cameFromOtherProduct || index !== 0) {
        return fields.slice(1);
      }

      return fields;
    }

    if (normalizedStep === ItemType.PARTIAL_DENTURE && useNewPapWorkflow) {
      if (isRdv2Continuation && isLabWorkflow) {
        const previousOrderType =
          previousProducts?.[normalizedStep]?.removableItem?.[0]
            ?.partialDentureOrderType;
        const isAfterTryIn =
          previousOrderType === RemovableItemOrderType.TRY_IN;

        if (isAfterTryIn) {
          return [{ rdv2Choice: "" }, { tryInFeedback: "" }];
        } else {
          const rdv2Fields = [
            { rdv2Choice: "" },
            {
              itemMaterialId: "",
              customTray: false,
              customTrayArch: null,
            },
            {
              shadeType: "",
              teethShadeId: "",
              gingivaShadeId: "",
              teethShape: "",
            },
          ];
          if (
            partialDentureOrderType &&
            !shouldShowPapAestheticsStep(partialDentureOrderType)
          ) {
            return rdv2Fields.filter((_, index) => index !== 2);
          }
          return rdv2Fields;
        }
      }

      const fields = getPartialDentureWorkflowFields(
        product?.removableItem ?? [],
        partialDentureOrderType as RemovableItemOrderType | null
      );

      if (cameFromOtherProduct || index !== 0) {
        return fields.slice(1);
      }
      return fields;
    }

    if (cameFromOtherProduct || index !== 0) {
      return workflowFields(product?.removableItem ?? [])[
        normalizedStep
      ]?.slice(1);
    }
    return workflowFields(product?.removableItem ?? [])[normalizedStep];
  });

  const currentWorkflowDefaultValues = workflow.map(step => {
    return workflowDefaultValues?.[step]?.defaultValues(order, product);
  });
  if (isLabWorkflow) {
    if (currentWorkflowFields.length === 0 || !currentWorkflowFields[0]) {
      return {
        workflowFields: [],
        workflowDefaultValues: currentWorkflowDefaultValues,
      };
    }
    const fields = currentWorkflowFields[0];

    // RDV2 fields don't have Patient/Recap - no need to slice
    if (isRdv2Continuation) {
      return {
        workflowFields: [fields],
        workflowDefaultValues: currentWorkflowDefaultValues,
      };
    }
    if (fields.length < 2) {
      // Not enough fields to remove first and last
      return {
        workflowFields: [fields],
        workflowDefaultValues: currentWorkflowDefaultValues,
      };
    }

    // remove patient and recap
    return {
      workflowFields: [fields.slice(1, fields.length - 1)],
      workflowDefaultValues: currentWorkflowDefaultValues,
    };
  }

  const values = workflow.map((step, index) => {
    const normalizedStep = step.split(" ")[0];
    const currentWorkflow =
      WORKFLOW_MAP[normalizedStep]?.steps(
        undefined, // removableItemChoicesData
        undefined, // nextStep
        undefined, // prevStep
        undefined, // product
        undefined, // refs (fullDentureRefs)
        undefined, // fullDentureOrderType
        isRdv2Continuation,
        previousProducts?.[normalizedStep],
        undefined, // isLabWorkflow
        undefined, // order
        useNewPapWorkflow,
        partialDentureRefs,
        partialDentureOrderType
      ) || [];

    if (!isLabWorkflow) {
      if (!cameFromOtherProduct && index === 0) {
        currentWorkflow.unshift({
          label: stepLabels.PATIENT,
          component: <></>,
          onNext: () => Promise.resolve(),
        });
      }
      if (index === workflow.length - 1) {
        currentWorkflow.push({
          label: stepLabels.RECAP,
          component: <></>,
          onNext: () => Promise.resolve(),
        });
      }
    }
    return currentWorkflow;
  });
  return {
    workflowFields: currentWorkflowFields,
    workflowDefaultValues: currentWorkflowDefaultValues,
    workflowMap: values,
  };
};

export const mapFieldsToValues = (fields: string[], values: string[]) => {
  return fields
    .map((field: string, index: number) => {
      return {
        [field]: values[index],
      };
    })
    .reduce((acc, curr) => {
      return { ...acc, ...curr };
    }, {});
};
